import * as v from 'valibot';

// Runtime environment validation schema
const envSchema = v.object({
  // Medplum configuration
  VITE_MEDPLUM_BASE_URL: v.pipe(
    v.string('VITE_MEDPLUM_BASE_URL is required'),
    v.url('VITE_MEDPLUM_BASE_URL must be a valid URL'),
  ),
  VITE_MEDPLUM_CLIENT_ID: v.pipe(
    v.string('VITE_MEDPLUM_CLIENT_ID is required'),
    v.minLength(1, 'VITE_MEDPLUM_CLIENT_ID cannot be empty'),
  ),
  // Development configuration
  VITE_APP_ENV: v.optional(
    v.union([
      v.literal('development'),
      v.literal('dev'),
      v.literal('qa'),
      v.literal('staging'),
      v.literal('production'),
    ]),
    'development',
  ),
  VITE_LOG_LEVEL: v.optional(
    v.union([
      v.literal('debug'),
      v.literal('info'),
      v.literal('warn'),
      v.literal('error'),
      v.literal('none'),
    ]),
    'info',
  ),
  // PostHog error tracking (optional — omit in local/test builds)
  VITE_PUBLIC_POSTHOG_KEY: v.optional(v.string()),
  VITE_PUBLIC_POSTHOG_HOST: v.optional(v.pipe(v.string(), v.url()), 'https://us.i.posthog.com'),
});

// Validate and export environment variables
function validateRuntimeEnv() {
  // Vite injects empty strings for unset build args (e.g. `--build-arg FOO=`).
  // Treat those as missing so `v.optional(..., default)` falls back to the default.
  const raw = Object.fromEntries(
    Object.entries(import.meta.env as Record<string, unknown>).map(([k, val]) => [
      k,
      val === '' ? undefined : val,
    ]),
  );
  try {
    return v.parse(envSchema, raw);
  } catch (error) {
    if (error instanceof v.ValiError) {
      // eslint-disable-next-line no-restricted-syntax
      console.error('Environment validation failed at runtime:');
      for (const issue of error.issues) {
        // @ts-expect-error unneeded check on issue path
        // eslint-disable-next-line no-restricted-syntax
        console.error(`${issue.path?.map((p) => p.key).join('.') || 'root'}: ${issue.message}`);
      }
      throw new Error('Invalid environment configuration');
    }
    throw error;
  }
}

// Validated environment variables
export const env = validateRuntimeEnv();

// Type export for use in other files
export type Env = typeof env;
